import { useCallback, useEffect, useRef, useState } from "react";
import { KioskIcon } from "./KioskIcon";

export const DEFAULT_KIOSK_SETTINGS = {
  homeScreenId: "",
  idleEnabled: true,
  idleTimeoutSec: 300,
  idlePromptEnabled: true,
  idlePromptSec: 10,
  idleTitle: "Are you still there?",
  idleMessage: "You have been inactive for {minutes} minutes. Do you want to continue on this screen or go back to the home screen?",
  idleContinueLabel: "Continue here",
  idleHomeLabel: "Go to Home",
  idleAccentColor: "#ef8a33",
};

export function kioskSettings(raw) {
  if (!raw) return { ...DEFAULT_KIOSK_SETTINGS, idleTimeoutSec: 120, idlePromptEnabled: false };
  return { ...DEFAULT_KIOSK_SETTINGS, ...raw };
}

export function homeScreenIdOf(data) {
  const screens = data?.screens || [];
  const id = data?.settings?.homeScreenId;
  return (id && screens.some(s => s.id === id)) ? id : screens[0]?.id || null;
}

const fmtDuration = sec => {
  if (sec % 60 === 0) return String(sec / 60);
  return (sec / 60).toFixed(1).replace(/\.0$/, "");
};

export function useKioskIdle({ settings, enabled = true, isHome, onHome, screenKey }) {
  const cfg = kioskSettings(settings);
  const [countdown, setCountdown] = useState(null);
  const timer = useRef(null);
  const tick = useRef(null);
  const onHomeRef = useRef(onHome);
  onHomeRef.current = onHome;
  const isHomeRef = useRef(isHome);
  isHomeRef.current = isHome;
  const promptOpen = useRef(false);
  promptOpen.current = countdown != null;

  const active = enabled && cfg.idleEnabled && Number(cfg.idleTimeoutSec) > 0;
  const timeoutMs = Math.max(5, Number(cfg.idleTimeoutSec) || 300) * 1000;
  const promptSec = Math.max(1, Number(cfg.idlePromptSec) || 10);
  const usePrompt = cfg.idlePromptEnabled !== false;

  const arm = useCallback(() => {
    clearTimeout(timer.current);
    if (!active) return;
    const fire = () => {
      if (usePrompt && !isHomeRef.current) { setCountdown(promptSec); return; }
      onHomeRef.current?.();
      timer.current = setTimeout(fire, timeoutMs);
    };
    timer.current = setTimeout(fire, timeoutMs);
  }, [active, timeoutMs, usePrompt, promptSec]);

  useEffect(() => { if (!promptOpen.current) arm(); }, [screenKey, arm]);

  useEffect(() => {
    if (!active) { clearTimeout(timer.current); setCountdown(null); return undefined; }
    const kick = () => { if (!promptOpen.current) arm(); };
    const events = ["pointerdown", "touchstart", "keydown", "wheel", "scroll", "mousemove"];
    events.forEach(e => window.addEventListener(e, kick, { passive: true, capture: true }));
    arm();
    return () => {
      clearTimeout(timer.current);
      events.forEach(e => window.removeEventListener(e, kick, { capture: true }));
    };
  }, [active, arm]);

  const open = countdown != null;
  useEffect(() => {
    clearInterval(tick.current);
    if (!open) return undefined;
    tick.current = setInterval(() => {
      setCountdown(c => {
        if (c == null) return c;
        if (c <= 1) {
          clearInterval(tick.current);
          setTimeout(() => { onHomeRef.current?.(); arm(); }, 0);
          return null;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick.current);
  }, [open, arm]);

  const stay = useCallback(() => { setCountdown(null); arm(); }, [arm]);
  const goHome = useCallback(() => { setCountdown(null); onHomeRef.current?.(); arm(); }, [arm]);

  return { countdown, stay, goHome, cfg };
}

export function IdlePrompt({ idle, width, height, zoom = 1 }) {
  if (!idle || idle.countdown == null) return null;
  const { cfg, countdown, stay, goHome } = idle;
  const accent = cfg.idleAccentColor || "#ef8a33";
  const minutes = fmtDuration(Math.max(5, Number(cfg.idleTimeoutSec) || 300));
  const message = String(cfg.idleMessage || "").replace(/\{minutes\}/g, minutes).replace(/\{seconds\}/g, String(cfg.idleTimeoutSec));
  const total = Math.max(1, Number(cfg.idlePromptSec) || 10);
  const boxW = Math.min(760, width * 0.8);
  const fs = Math.max(14, Math.round(boxW / 30));
  return <div style={{ position: "absolute", left: 0, top: 0, width, height, transform: `scale(${zoom})`, transformOrigin: "top left", zIndex: 9500, pointerEvents: "auto" }}>
    <style>{"@keyframes kioskIdleIn{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:none}}"}</style>
    <div onPointerDown={e => { if (e.target === e.currentTarget) stay(); }} style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, sans-serif" }}>
      <div style={{ width: boxW, background: "#ffffff", borderRadius: fs * 1.2, padding: fs * 2, boxSizing: "border-box", boxShadow: "0 30px 80px rgba(0,0,0,0.35)", animation: "kioskIdleIn 0.22s ease-out", textAlign: "center", color: "#111827" }}>
        <div style={{ width: fs * 3.4, height: fs * 3.4, margin: "0 auto", borderRadius: "50%", background: `${accent}22`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
          <svg width={fs * 3.4} height={fs * 3.4} style={{ position: "absolute", inset: 0, transform: "rotate(-90deg)" }}>
            <circle cx={fs * 1.7} cy={fs * 1.7} r={fs * 1.55} fill="none" stroke={accent} strokeWidth={fs * 0.22} strokeLinecap="round"
              strokeDasharray={2 * Math.PI * fs * 1.55} strokeDashoffset={2 * Math.PI * fs * 1.55 * (1 - countdown / total)} style={{ transition: "stroke-dashoffset 1s linear" }} />
          </svg>
          <span style={{ fontSize: fs * 1.3, fontWeight: 700, color: accent }}>{countdown}</span>
        </div>
        <div style={{ fontSize: fs * 1.45, fontWeight: 700, marginTop: fs * 1.1 }}>{cfg.idleTitle}</div>
        <div style={{ fontSize: fs, color: "#4b5563", lineHeight: 1.55, marginTop: fs * 0.6 }}>{message}</div>
        <div style={{ fontSize: fs * 0.8, color: "#6b7280", marginTop: fs * 0.6 }}>Returning to the home screen in {countdown}s</div>
        <div style={{ display: "flex", gap: fs * 0.8, justifyContent: "center", marginTop: fs * 1.5 }}>
          <button type="button" onClick={stay} style={{ flex: 1, padding: `${fs * 0.75}px ${fs}px`, borderRadius: fs * 0.7, border: `2px solid ${accent}`, background: "#ffffff", color: "#111827", fontSize: fs, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: fs * 0.5 }}>
            <KioskIcon name="Check" size={fs * 1.1} color="#111827" />{cfg.idleContinueLabel}
          </button>
          <button type="button" onClick={goHome} style={{ flex: 1, padding: `${fs * 0.75}px ${fs}px`, borderRadius: fs * 0.7, border: `2px solid ${accent}`, background: accent, color: "#ffffff", fontSize: fs, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: fs * 0.5 }}>
            <KioskIcon name="House" size={fs * 1.1} color="#ffffff" />{cfg.idleHomeLabel}
          </button>
        </div>
      </div>
    </div>
  </div>;
}
